/**
 * Utility for handling JWT token refresh scenarios
 *
 * IMPORTANT: In this application architecture:
 * - JWT tokens come from SRP authentication (idToken, accessToken, refreshToken)
 * - idToken is used for Cognito Identity Pool (S3) authentication
 * - accessToken is used for WebSocket authentication
 * - refreshToken is used to get new tokens when they expire
 *
 * This manager provides JWT token refresh functionality using Cognito refresh tokens.
 */

import {
  CognitoUserPool,
  CognitoUser,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";

class TokenRefreshManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Refreshes JWT tokens using the refreshToken from Cognito
   * @param {Object} user - Current user object with refreshToken
   * @returns {Promise<Object>} Updated user object with new tokens
   */
  async refreshUserToken(user) {
    // If already handling refresh, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if we have a refreshToken
    if (!user?.refreshToken) {
      console.warn("No refreshToken available for token refresh");
      return user;
    }

    this.isRefreshing = true;

    this.refreshPromise = this._performTokenRefresh(user);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async _performTokenRefresh(user) {

    try {
      const poolData = {
        UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      };

      const userPool = new CognitoUserPool(poolData);
      const cognitoUser = new CognitoUser({
        Username: user.username,
        Pool: userPool,
      });

      const refreshToken = new CognitoRefreshToken({
        RefreshToken: user.refreshToken,
      });

      return new Promise((resolve, reject) => {
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            console.error("Token refresh failed:", err);
            reject(err);
            return;
          }

          // Get new tokens from the refreshed session
          const newIdToken = session.getIdToken().getJwtToken();
          const newAccessToken = session.getAccessToken().getJwtToken();
          const newRefreshToken = session.getRefreshToken().getToken();

          // Update user object with new tokens
          const updatedUser = {
            ...user,
            idToken: newIdToken,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            tokenRefreshedAt: Date.now(),
            tokenRefreshAttempts: 0, // Reset attempts on successful refresh
          };

          // Update localStorage
          localStorage.setItem("user", JSON.stringify(updatedUser));

          resolve(updatedUser);
        });
      });
    } catch (error) {
      console.error("Token refresh error:", error);
      throw error;
    }
  }

  /**
   * Checks if the user's JWT tokens are likely expired
   * @param {Object} user - User object to check
   * @returns {boolean} True if tokens might be expired
   */
  isTokenLikelyExpired(user) {
    if (!user || !user.idToken) {
      return true; // No tokens available
    }

    // Try to decode the JWT token to check expiration
    try {
      const tokenParts = user.idToken.split('.');
      if (tokenParts.length !== 3) {
        return true; // Invalid token format
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Consider token expired if it expires within the next 5 minutes
      return payload.exp < (currentTime + 300);
    } catch (error) {
      console.warn("Error decoding JWT token for expiration check:", error);
      // Fallback: check timestamp if available
      if (user.tokenRefreshedAt) {
        const now = Date.now();
        const tokenAge = now - user.tokenRefreshedAt;
        // Consider token expired after 45 minutes (AWS Cognito tokens typically last 1 hour)
        return tokenAge > (45 * 60 * 1000);
      }
      return true; // Can't determine expiration
    }
  }

  /**
   * Gets the expiration time from a JWT token
   * @param {string} token - JWT token
   * @returns {number|null} Expiration timestamp in seconds, or null if invalid
   */
  getTokenExpiration(token) {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return null;

      const payload = JSON.parse(atob(tokenParts[1]));
      return payload.exp;
    } catch (error) {
      return null;
    }
  }

  /**
   * Proactively refreshes tokens if they're close to expiring
   * @param {Object} user - User object to check and refresh
   * @returns {Promise<Object>} Updated user object (refreshed if needed)
   */
  async ensureValidTokens(user) {
    if (!user?.idToken) {
      return user;
    }

    // Check if tokens are close to expiring (within 10 minutes)
    const expiration = this.getTokenExpiration(user.idToken);
    if (!expiration) {
      return user;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiration = expiration - currentTime;

    // Refresh if expires within 10 minutes
    if (timeUntilExpiration < 600) {
      try {
        return await this.refreshUserToken(user);
      } catch (error) {
        console.warn("Proactive token refresh failed:", error);
        return user;
      }
    }

    return user;
  }
}

// Export a singleton instance
export const tokenRefreshManager = new TokenRefreshManager();
